// supabaseStorage.ts — Phase 2 Supabase read/write layer
// All functions are no-ops when Supabase is not configured.
// The localStorage path (storage.ts) remains unchanged and runs in parallel.

import { supabase, isSupabaseEnabled } from "./supabase";
import type {
  PropertyRecord,
  IncomeRow,
  ExpenseRow,
  EquityRelease,
  Div40Item,
  LowValuePool,
  DepreciationSchedule,
} from "../types/portfolio";
import type { Structure, InvestorProfile } from "../types/entities";
import type { BudgetSettings, BudgetCategory } from "../types/budget";
import type { AppSettings } from "../components/settings/SettingsPage";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const dateOrNull = (v: unknown): string | null =>
  v && typeof v === "string" && v !== "" ? v : null;

function guard(): boolean {
  return isSupabaseEnabled() && !!supabase;
}

/**
 * Read-time normalization for the legacy single-object equity_release shape.
 * Pre-Day-35 rows stored a single `{amount, arrangement, destinationStructureId}`
 * object. New rows store an array of EquityRelease tranches. This helper
 * accepts either shape and returns the canonical array form (or undefined).
 *
 * Defensive: also injects an `id` field if any release in an existing array
 * is missing one (e.g. data hand-edited via SQL).
 */
/** Map a raw JSONB object to a typed EquityRelease, preserving all fields
 * including the 12 per-tranche loan detail fields added Day 37. */
function mapRawToEquityRelease(obj: Record<string, unknown>): EquityRelease {
  return {
    id: (obj.id as string) ?? crypto.randomUUID(),
    destinationStructureId: (obj.destinationStructureId as string) ?? "",
    destinationPropertyId: obj.destinationPropertyId as string | undefined,
    amount: (obj.amount as number) ?? 0,
    arrangement: (obj.arrangement as "formal-loan" | "informal") ?? "informal",
    date: obj.date as string | undefined,
    notes: obj.notes as string | undefined,
    // Per-tranche loan details (Day 37)
    lender: obj.lender as string | undefined,
    loanAccountNumber: obj.loanAccountNumber as string | undefined,
    balance: obj.balance as number | undefined,
    rate: obj.rate as number | undefined,
    rateType: obj.rateType as string | undefined,
    repaymentType: obj.repaymentType as string | undefined,
    loanTermYears: obj.loanTermYears as number | undefined,
    ioTermYears: obj.ioTermYears as number | undefined,
    loanStartDate: obj.loanStartDate as string | undefined,
    fixedRateExpiryDate: obj.fixedRateExpiryDate as string | undefined,
    offsetBalance: obj.offsetBalance as number | undefined,
    offsetAccount: obj.offsetAccount as string | undefined,
    // Phase 3 (Day 43) drawdown fields
    creditLimit: obj.creditLimit as number | undefined,
    drawdownAmount: obj.drawdownAmount as number | undefined,
    drawdownFrequency: obj.drawdownFrequency as EquityRelease["drawdownFrequency"],
  };
}

function normalizeEquityRelease(value: unknown): EquityRelease[] | undefined {
  if (value === null || value === undefined) return undefined;
  // Legacy single-object shape — wrap as one-element array
  if (typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (obj.amount === undefined && obj.arrangement === undefined) return undefined;
    return [mapRawToEquityRelease(obj)];
  }
  if (!Array.isArray(value)) return undefined;
  return value.map((r) => mapRawToEquityRelease(r as Record<string, unknown>));
}

/**
 * Map a raw JSONB Div 40 item to a typed `Div40Item`. Closes the L26
 * gap on `depreciation_schedule` reads — Wave 2C Phase 1 extends
 * `Div40Item` from 5 to 17 fields, so explicit per-field enumeration
 * becomes mandatory (whole-blob spread silently drops unknown fields,
 * which now would mean dropping every Wave 2C extension).
 *
 * Defensive id injection mirrors `mapRawToEquityRelease` (line ~32) for
 * SQL-hand-edited rows that may not carry an id.
 */
function mapRawToDiv40Item(obj: Record<string, unknown>): Div40Item {
  return {
    // Existing 5 required fields
    id: (obj.id as string) ?? crypto.randomUUID(),
    name: (obj.name as string) ?? "",
    cost: (obj.cost as number) ?? 0,
    effectiveLife: (obj.effectiveLife as number) ?? 0,
    method: (obj.method as Div40Item["method"]) ?? "diminishing-value",
    // Wave 2C Phase 1 extensions
    assetCode: obj.assetCode as string | undefined,
    category: obj.category as string | undefined,
    acquisitionDate: obj.acquisitionDate as string | undefined,
    isSecondHand: obj.isSecondHand as boolean | undefined,
    secondHandExceptionReason: obj.secondHandExceptionReason as Div40Item["secondHandExceptionReason"],
    effectiveLifeOverridden: obj.effectiveLifeOverridden as boolean | undefined,
    poolAllocated: obj.poolAllocated as boolean | undefined,
    poolAllocationDate: obj.poolAllocationDate as string | undefined,
    immediateDeductionTaken: obj.immediateDeductionTaken as boolean | undefined,
    disposalDate: obj.disposalDate as string | undefined,
    terminationValue: obj.terminationValue as number | undefined,
    fyOverrides: Array.isArray(obj.fyOverrides)
      ? obj.fyOverrides.map((r) => ({
          fy: ((r as Record<string, unknown>).fy as string) ?? "",
          claimAmount: ((r as Record<string, unknown>).claimAmount as number) ?? 0,
          source: ((r as Record<string, unknown>).source as string) ?? "",
        }))
      : undefined,
  };
}

/** Map a raw JSONB low-value pool entry to a typed `LowValuePool`.
 *  Defensive id injection mirrors `mapRawToEquityRelease`. */
function mapRawToLowValuePool(obj: Record<string, unknown>): LowValuePool {
  return {
    id: (obj.id as string) ?? crypto.randomUUID(),
    fy: (obj.fy as string) ?? "",
    openingBalance: (obj.openingBalance as number) ?? 0,
    additionsThisFY: (obj.additionsThisFY as number) ?? 0,
    closingBalance: (obj.closingBalance as number) ?? 0,
  };
}

/**
 * Map a raw JSONB `depreciation_schedule` payload to a typed
 * `DepreciationSchedule`. Closes the L26 reader-enumeration gap that
 * was silently shipping since the field's introduction — fine while
 * `DepreciationSchedule` carried only ~7 simple primitives, but Wave
 * 2C Phase 1's `Div40Item` extension to 17 fields plus the new
 * `LowValuePool` ledger makes whole-blob `?? undefined` spread unsafe
 * (any unrecognised field would silently disappear).
 *
 * Exported so the round-trip tests can exercise it in isolation
 * (no Supabase dependency).
 */
export function mapRawToDepreciationSchedule(value: unknown): DepreciationSchedule | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  return {
    div43ConstructionCost: obj.div43ConstructionCost as number | undefined,
    div43DateCommenced: obj.div43DateCommenced as string | undefined,
    div43DateCompleted: obj.div43DateCompleted as string | undefined,
    div43Rate: obj.div43Rate as number | undefined,
    div43RateOverridden: obj.div43RateOverridden as boolean | undefined,
    div40Items: Array.isArray(obj.div40Items)
      ? obj.div40Items.map((r) => mapRawToDiv40Item(r as Record<string, unknown>))
      : undefined,
    div40Method: obj.div40Method as DepreciationSchedule["div40Method"],
    lowValuePools: Array.isArray(obj.lowValuePools)
      ? obj.lowValuePools.map((r) => mapRawToLowValuePool(r as Record<string, unknown>))
      : undefined,
  };
}

// ─── Property mapping ─────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToProperty(row: any): PropertyRecord {
  // Reconstruct [DRAFT SCENARIO] prefix from the is_draft boolean
  const isDraft = !!row.is_draft;
  const rawNotes = row.notes ?? "";
  const notes = isDraft
    ? `[DRAFT SCENARIO]${rawNotes ? " " + rawNotes : ""}`.trimEnd()
    : rawNotes;

  return {
    id: row.id,
    nickname: row.nickname ?? "",
    address: row.address ?? "",
    suburb: row.suburb ?? "",
    state: row.state ?? "",
    postcode: row.postcode ?? "",
    assetClass: row.asset_class ?? "Residential",
    propertyType: row.property_type ?? "House",
    purchaseDate: row.purchase_date ?? "",
    // B130 Wave 2A — CGT event date per RPG 2025 p62. Phase 1
    // migration backfilled contract_date from purchase_date for
    // legacy rows; Phase 6 form will let users refine where the
    // contract and settlement dates differ.
    contractDate: row.contract_date ?? "",
    purchasePrice: row.purchase_price ?? 0,
    buyCosts: row.buy_costs ?? 0,
    legalBuyCosts: row.legal_buy_costs ?? 0,
    currentValue: row.current_value ?? 0,
    weeklyRent: row.weekly_rent ?? 0,
    loanBalance: row.loan_balance ?? 0,
    structureId: row.structure_id ?? "",
    owner: row.structure_id ?? "",
    lender: row.lender ?? "",
    loanAccountNumber: row.loan_account_number ?? "",
    offsetAccount: row.offset_account ?? "",
    loanStartDate: row.loan_start_date ?? "",
    loanTermYears: row.loan_term_years ?? 30,
    ioTermYears: row.io_term_years ?? 0,
    rate: row.rate ?? 0,
    repaymentType: row.repayment_type ?? "P&I",
    rateType: row.rate_type ?? "Variable",
    monthlyExpenses: row.monthly_expenses ?? 0,
    annualRates: row.annual_rates ?? 0,
    annualInsurance: row.annual_insurance ?? 0,
    annualStrata: row.annual_strata ?? 0,
    annualLandTax: row.annual_land_tax ?? 0,
    annualMaintenance: row.annual_maintenance ?? 0,
    annualPropertyManagement: row.annual_property_management ?? 0,
    personalTopUp: row.personal_top_up ?? 0,
    estimatedSellingCostsPct: row.estimated_selling_costs_pct ?? 2.5,
    customExpenses: row.custom_expenses ?? [],
    customTabs: row.custom_tabs ?? [],
    notes,
    ownershipPercent: row.ownership_percent ?? 100,
    // B103 Phase 1 — household ownership model
    ownershipMode: row.ownership_mode ?? undefined,
    coOwners: row.co_owners ?? [],
    offsetBalance: row.offset_balance ?? undefined,
    fixedRateExpiryDate: row.fixed_rate_expiry_date ?? undefined,
    loanBalanceUpdatedAt: row.loan_balance_updated_at ?? undefined,
    splitLoan: row.split_loan ?? undefined,
    splitLoanBalance: row.split_loan_balance ?? undefined,
    splitLoanRate: row.split_loan_rate ?? undefined,
    splitLoanRateType: row.split_loan_rate_type ?? undefined,
    splitLoanRepaymentType: row.split_loan_repayment_type ?? undefined,
    splitLoanTermYears: row.split_loan_term_years ?? undefined,
    splitLoanFixedRateExpiryDate: row.split_loan_fixed_rate_expiry_date ?? undefined,
    imageUrl: row.image_url ?? undefined,
    rentEscalationType: row.rent_escalation_type ?? undefined,
    rentEscalationRate: row.rent_escalation_rate ?? undefined,
    equityReleases: normalizeEquityRelease(row.equity_release),
    incomeRows: row.income_rows ?? [],
    expenseRows: row.expense_rows ?? [],
    acquisitionCosts: row.acquisition_costs ?? [],
    capitalImprovements: row.capital_improvements ?? [],
    depreciationClaimed: row.depreciation_claimed ?? [],
    depreciationSchedule: mapRawToDepreciationSchedule(row.depreciation_schedule),
    insurancePolicies: row.insurance_policies ?? [],
    documents: row.documents ?? [],
    changeLog: row.change_log ?? [],
    totalBorrowingExpensesClaimed: row.total_borrowing_expenses_claimed ?? undefined,
    borrowingCosts: row.borrowing_costs ?? undefined,
    borrowingCostsStartDate: row.borrowing_costs_start_date ?? undefined,
    leaseStartDate: row.lease_start_date ?? undefined,
    leaseEndDate: row.lease_end_date ?? undefined,
    tenantName: row.tenant_name ?? undefined,
    tenantPhone: row.tenant_phone ?? undefined,
    bondHeld: row.bond_held ?? undefined,
    managementFeePercent: row.management_fee_percent ?? undefined,
    lettingFeeWeeks: row.letting_fee_weeks ?? undefined,
    renewalFeeWeeks: row.renewal_fee_weeks ?? undefined,
    propertyManagerName: row.property_manager_name ?? undefined,
    propertyManagerEmail: row.property_manager_email ?? undefined,
    propertyManagerPhone: row.property_manager_phone ?? undefined,
    holdingBuffer: row.holding_buffer ?? undefined,
    holdingBufferStartDate: row.holding_buffer_start_date ?? undefined,
    rentReviewDate: row.rent_review_date ?? undefined,
    smokeAlarmExpiryDate: row.smoke_alarm_expiry_date ?? undefined,
    poolSafetyCertDate: row.pool_safety_cert_date ?? undefined,
    councilRatesDueDate: row.council_rates_due_date ?? undefined,
    waterRatesDueDate: row.water_rates_due_date ?? undefined,
    strataLevyDueDate: row.strata_levy_due_date ?? undefined,
    lastValuationDate: row.last_valuation_date ?? undefined,
    marketValue: row.market_value ?? row.current_value ?? undefined,
    bankValuation: row.bank_valuation ?? undefined,
    unimprovedLandValue: row.unimproved_land_value ?? null,
    accountName: row.account_name ?? undefined,
    bsb: row.bsb ?? undefined,
    accountNumber: row.account_number ?? undefined,
    status: row.status ?? "active",
    saleDate: row.sale_date ?? undefined,
    // B130 Wave 2A — sale CGT event date per RPG 2025 p62. Phase 6
    // form will add the input; until then this round-trips DB null.
    saleContractDate: row.sale_contract_date ?? undefined,
    salePrice: row.sale_price ?? undefined,
    saleAgentFees: row.sale_agent_fees ?? undefined,
    saleLegalCosts: row.sale_legal_costs ?? undefined,
    saleOtherCosts: row.sale_other_costs ?? undefined,
    saleNotes: row.sale_notes ?? undefined,
    // B130 Wave 2C Phase 11 — PPOR-converted-to-rental flag (s40-27(3)).
    pporConvertedFromPpor: row.ppor_converted_from_ppor ?? undefined,
    // HTAG Intelligence fields
    lgaId: row.lga_id ?? null,
    lgaName: row.lga_name ?? null,
    addressKey: row.address_key ?? null,
    // Geolocation
    latitude: row.latitude ?? null,
    longitude: row.longitude ?? null,
    // N1 — value/rent history
    valueHistory: row.value_history ?? [],
    rentHistory: row.rent_history ?? [],
  };
}

function propertyToRow(p: PropertyRecord, userId: string) {
  const isDraft = !!(p.notes?.includes("[DRAFT SCENARIO]"));
  const cleanNotes = isDraft
    ? (p.notes ?? "")
        .replace("[DRAFT SCENARIO] ", "")
        .replace("[DRAFT SCENARIO]", "")
        .trim()
    : (p.notes ?? "");

  return {
    id: p.id,
    user_id: userId,
    structure_id: p.structureId || null,
    nickname: p.nickname ?? null,
    address: p.address ?? null,
    suburb: p.suburb ?? null,
    state: p.state ?? null,
    postcode: p.postcode ?? null,
    asset_class: p.assetClass ?? null,
    property_type: p.propertyType ?? null,
    purchase_date: dateOrNull(p.purchaseDate),
    // B130 Wave 2A — round-trip safe: rowToProperty seeds contractDate
    // from the DB column (Phase 1 backfilled it from purchase_date),
    // and we write it back here. When Phase 6 form lights up the explicit
    // input this fallback continues to keep legacy rows in sync.
    contract_date: dateOrNull(p.contractDate ?? p.purchaseDate),
    purchase_price: p.purchasePrice ?? null,
    buy_costs: p.buyCosts ?? null,
    legal_buy_costs: p.legalBuyCosts ?? null,
    current_value: p.currentValue ?? null,
    weekly_rent: p.weeklyRent ?? null,
    loan_balance: p.loanBalance ?? null,
    lender: p.lender ?? null,
    loan_account_number: p.loanAccountNumber ?? null,
    rate: p.rate ?? null,
    repayment_type: p.repaymentType ?? null,
    rate_type: p.rateType ?? null,
    loan_start_date: dateOrNull(p.loanStartDate),
    loan_term_years: p.loanTermYears ?? null,
    io_term_years: p.ioTermYears ?? null,
    offset_account: p.offsetAccount ?? null,
    monthly_expenses: p.monthlyExpenses ?? null,
    annual_rates: p.annualRates ?? null,
    annual_insurance: p.annualInsurance ?? null,
    annual_strata: p.annualStrata ?? null,
    annual_land_tax: p.annualLandTax ?? null,
    annual_maintenance: p.annualMaintenance ?? null,
    annual_property_management: p.annualPropertyManagement ?? null,
    personal_top_up: p.personalTopUp ?? null,
    estimated_selling_costs_pct: p.estimatedSellingCostsPct ?? null,
    ownership_percent: p.ownershipPercent ?? 100,
    // B103 Phase 1 — household ownership model
    ownership_mode: p.ownershipMode ?? null,
    co_owners: p.coOwners ?? [],
    offset_balance: p.offsetBalance ?? null,
    fixed_rate_expiry_date: dateOrNull(p.fixedRateExpiryDate),
    loan_balance_updated_at: dateOrNull(p.loanBalanceUpdatedAt),
    split_loan: p.splitLoan ?? null,
    split_loan_balance: p.splitLoanBalance ?? null,
    split_loan_rate: p.splitLoanRate ?? null,
    split_loan_rate_type: p.splitLoanRateType ?? null,
    split_loan_repayment_type: p.splitLoanRepaymentType ?? null,
    split_loan_term_years: p.splitLoanTermYears ?? null,
    split_loan_fixed_rate_expiry_date: dateOrNull(p.splitLoanFixedRateExpiryDate),
    rent_escalation_type: p.rentEscalationType ?? null,
    rent_escalation_rate: p.rentEscalationRate ?? null,
    equity_release: p.equityReleases && p.equityReleases.length > 0 ? p.equityReleases : null,
    custom_expenses: p.customExpenses ?? [],
    income_rows: p.incomeRows ?? [],
    expense_rows: p.expenseRows ?? [],
    acquisition_costs: p.acquisitionCosts ?? [],
    capital_improvements: p.capitalImprovements ?? [],
    depreciation_claimed: p.depreciationClaimed ?? [],
    // Per L26: writer is permissive (whole-blob serialised), reader
    // (mapRawToDepreciationSchedule) is explicit per-field. Asymmetry
    // is intentional — JSONB column accepts whatever shape we send,
    // but the read path is the canonical source of truth for which
    // fields are recognised.
    depreciation_schedule: p.depreciationSchedule ?? null,
    insurance_policies: p.insurancePolicies ?? [],
    documents: p.documents ?? [],
    change_log: p.changeLog ?? [],
    custom_tabs: p.customTabs?.length ? p.customTabs : null,
    is_draft: isDraft,
    notes: cleanNotes || null,
    image_url: p.imageUrl ?? null,
    total_borrowing_expenses_claimed: p.totalBorrowingExpensesClaimed ?? null,
    borrowing_costs: p.borrowingCosts ?? null,
    borrowing_costs_start_date: dateOrNull(p.borrowingCostsStartDate),
    lease_start_date: dateOrNull(p.leaseStartDate),
    lease_end_date: dateOrNull(p.leaseEndDate),
    tenant_name: p.tenantName ?? null,
    tenant_phone: p.tenantPhone ?? null,
    bond_held: p.bondHeld ?? null,
    management_fee_percent: p.managementFeePercent ?? null,
    letting_fee_weeks: p.lettingFeeWeeks ?? null,
    renewal_fee_weeks: p.renewalFeeWeeks ?? null,
    property_manager_name: p.propertyManagerName ?? null,
    property_manager_email: p.propertyManagerEmail ?? null,
    property_manager_phone: p.propertyManagerPhone ?? null,
    holding_buffer: p.holdingBuffer ?? null,
    holding_buffer_start_date: dateOrNull(p.holdingBufferStartDate),
    rent_review_date: dateOrNull(p.rentReviewDate),
    smoke_alarm_expiry_date: dateOrNull(p.smokeAlarmExpiryDate),
    pool_safety_cert_date: dateOrNull(p.poolSafetyCertDate),
    council_rates_due_date: dateOrNull(p.councilRatesDueDate),
    water_rates_due_date: dateOrNull(p.waterRatesDueDate),
    strata_levy_due_date: dateOrNull(p.strataLevyDueDate),
    last_valuation_date: dateOrNull(p.lastValuationDate),
    market_value: p.marketValue ?? null,
    bank_valuation: p.bankValuation ?? null,
    unimproved_land_value: p.unimprovedLandValue ?? null,
    account_name: p.accountName ?? null,
    bsb: p.bsb ?? null,
    account_number: p.accountNumber ?? null,
    status: p.status ?? "active",
    sale_date: dateOrNull(p.saleDate),
    // B130 Wave 2A — sale CGT event date. Round-trip safe: when Phase 6
    // form is added, users refine the contract date independently;
    // until then we mirror sale_date so legacy rows stay consistent.
    sale_contract_date: dateOrNull(p.saleContractDate ?? p.saleDate),
    sale_price: p.salePrice ?? null,
    sale_agent_fees: p.saleAgentFees ?? null,
    sale_legal_costs: p.saleLegalCosts ?? null,
    sale_other_costs: p.saleOtherCosts ?? null,
    sale_notes: p.saleNotes ?? null,
    // B130 Wave 2C Phase 11 — PPOR-converted-to-rental flag (s40-27(3)).
    ppor_converted_from_ppor: p.pporConvertedFromPpor ?? null,
    // HTAG Intelligence fields
    lga_id: p.lgaId ?? null,
    lga_name: p.lgaName ?? null,
    address_key: p.addressKey ?? null,
    // Geolocation
    latitude: p.latitude ?? null,
    longitude: p.longitude ?? null,
    // N1 — value/rent history
    value_history: p.valueHistory ?? [],
    rent_history: p.rentHistory ?? [],
  };
}

// ─── Structure mapping ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToStructure(row: any): Structure {
  return {
    id: row.id,
    entityType: row.entity_type,
    displayName: row.display_name ?? "",
    trusteeName: row.trustee_name ?? "",
    trusteeType: row.trustee_type ?? "",
    companyName: row.company_name ?? "",
    trustName: row.trust_name ?? "",
    trustType: row.trust_type ?? "",
    abn: row.abn ?? "",
    tfn: row.tfn ?? "",
    acn: row.acn ?? undefined,
    gstRegistered: row.gst_registered ?? false,
    dateEstablished: row.date_established ?? undefined,
    stateOfEstablishment: row.state_of_establishment ?? undefined,
    registeredOfficeAddress: row.registered_office_address ?? undefined,
    familyTrustElection: row.family_trust_election ?? false,
    memberNames: row.member_names ?? undefined,
    beneficiaries: row.beneficiaries ?? [],
    documents: row.documents ?? [],
    selfFundingOverride: row.self_funding_override ?? null,
    monthlyContributions: row.monthly_contributions ?? undefined,
    transactionBankName: row.transaction_bank_name ?? undefined,
    transactionBSB: row.transaction_bsb ?? undefined,
    transactionAccountName: row.transaction_account_name ?? undefined,
    transactionAccountNumber: row.transaction_account_number ?? undefined,
    redistributionMode: row.redistribution_mode ?? "hold",
    // B130 Wave 2C Phase 4 — s40-27(2) excluded-entity flag. Round-trips
    // through structures.excluded_entity_type column (Phase 4 migration).
    excludedEntityType:
      (row.excluded_entity_type ?? null) as Structure["excludedEntityType"],
    // B125 — trust loss carry-forward ledger (jsonb).
    lossCarryForward: row.loss_carry_forward ?? null,
  };
}

function structureToRow(s: Structure, userId: string) {
  return {
    id: s.id,
    user_id: userId,
    entity_type: s.entityType,
    display_name: s.displayName,
    trustee_name: s.trusteeName ?? null,
    trustee_type: s.trusteeType ?? null,
    company_name: s.companyName ?? null,
    trust_name: s.trustName ?? null,
    trust_type: s.trustType ?? null,
    abn: s.abn ?? null,
    tfn: s.tfn ?? null,
    acn: s.acn ?? null,
    gst_registered: s.gstRegistered ?? false,
    date_established: dateOrNull(s.dateEstablished),
    state_of_establishment: s.stateOfEstablishment ?? null,
    registered_office_address: s.registeredOfficeAddress ?? null,
    family_trust_election: s.familyTrustElection ?? false,
    member_names: s.memberNames ?? null,
    beneficiaries: s.beneficiaries ?? [],
    documents: s.documents ?? [],
    self_funding_override: s.selfFundingOverride ?? null,
    monthly_contributions: s.monthlyContributions ?? null,
    transaction_bank_name: s.transactionBankName ?? null,
    transaction_bsb: s.transactionBSB ?? null,
    transaction_account_name: s.transactionAccountName ?? null,
    transaction_account_number: s.transactionAccountNumber ?? null,
    redistribution_mode: s.redistributionMode ?? "hold",
    excluded_entity_type: s.excludedEntityType ?? null,
    // B125 — trust loss carry-forward ledger (jsonb). Null when ledger empty.
    loss_carry_forward: s.lossCarryForward ?? null,
  };
}

// ─── Profile mapping ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToProfile(row: any): InvestorProfile {
  return {
    fullLegalName: row.full_legal_name ?? undefined,
    preferredName: row.preferred_name ?? undefined,
    dateOfBirth: row.date_of_birth ?? undefined,
    gender: row.gender ?? undefined,
    currentAddress: row.current_address ?? undefined,
    phone: row.phone ?? undefined,
    email: row.email ?? undefined,
    tfn: row.tfn ?? undefined,
    citizenshipStatus: row.citizenship_status ?? undefined,
    taxResidencyStatus: row.tax_residency_status ?? undefined,
    annualPreTaxIncome: row.annual_pre_tax_income ?? undefined,
    employmentType: row.employment_type ?? undefined,
    employerName: row.employer_name ?? undefined,
    employerAbn: row.employer_abn ?? undefined,
    marginalTaxRate: row.marginal_tax_rate ?? undefined,
    monthlyNetContribution: row.monthly_net_contribution ?? undefined,
    medicareExempt: row.medicare_exempt ?? false,
    medicareReduction: row.medicare_reduction ?? false,
    hasPrivateHospitalCover: row.has_private_hospital_cover ?? false,
    medicareLevy: row.medicare_levy ?? undefined,
    hasHECS: row.has_hecs ?? false,
    strategyTone: row.strategy_tone ?? undefined,
    portfolioPhase: row.portfolio_phase ?? undefined,
    phaseOverride: row.phase_override ?? false,
    primaryStrategy: row.primary_strategy ?? undefined,
    secondaryStrategy: row.secondary_strategy ?? undefined,
    visionStatement: row.vision_statement ?? undefined,
    currentConstraint: row.current_constraint ?? undefined,
    goalFeasibility: row.goal_feasibility ?? undefined,
    goalIsCustom: row.goal_is_custom ?? false,
    goalNotes: row.goal_notes ?? undefined,
    passiveIncomeTarget: row.passive_income_target ?? undefined,
    portfolioValueTarget: row.portfolio_value_target ?? undefined,
    portfolioTargetDate: row.portfolio_target_date ?? undefined,
    retirementAge: row.retirement_age ?? undefined,
    retirementIncomeTarget: row.retirement_income_target ?? undefined,
    pporBalance: row.ppor_balance ?? undefined,
    pporPayoffDate: row.ppor_payoff_date ?? undefined,
    reducedIncomePeriods: row.reduced_income_periods ?? [],
    householdMembers: row.household_members ?? [],
    relationships: row.relationships ?? [],
    teamContacts: row.team_contacts ?? [],
    verificationDocuments: row.verification_documents ?? [],
    taxDocuments: row.tax_documents ?? [],
    payslips: row.payslips ?? [],
    paygVariationApproved: row.payg_variation_approved ?? false,
    paygVariationOverride: row.payg_variation_override ?? undefined,
    // B103 Phase 4b — closes the gap where DashboardPage read the field but
    // the mapper silently dropped it on save (column added in
    // 20260502130000_add_profiles_payg_withheld.sql).
    paygWithheld: row.payg_withheld ?? undefined,
  };
}

function profileToRow(p: InvestorProfile, userId: string) {
  return {
    user_id: userId,
    full_legal_name: p.fullLegalName ?? null,
    preferred_name: p.preferredName ?? null,
    date_of_birth: dateOrNull(p.dateOfBirth),
    gender: p.gender ?? null,
    current_address: p.currentAddress ?? null,
    phone: p.phone ?? null,
    email: p.email ?? null,
    tfn: p.tfn ?? null,
    citizenship_status: p.citizenshipStatus ?? null,
    tax_residency_status: p.taxResidencyStatus ?? null,
    annual_pre_tax_income: p.annualPreTaxIncome ?? null,
    employment_type: p.employmentType ?? null,
    employer_name: p.employerName ?? null,
    employer_abn: p.employerAbn ?? null,
    marginal_tax_rate: p.marginalTaxRate ?? null,
    monthly_net_contribution: p.monthlyNetContribution ?? null,
    medicare_exempt: p.medicareExempt ?? false,
    medicare_reduction: p.medicareReduction ?? false,
    has_private_hospital_cover: p.hasPrivateHospitalCover ?? false,
    medicare_levy: p.medicareLevy ?? null,
    has_hecs: p.hasHECS ?? false,
    strategy_tone: p.strategyTone ?? null,
    portfolio_phase: p.portfolioPhase ?? null,
    phase_override: p.phaseOverride ?? false,
    primary_strategy: p.primaryStrategy ?? null,
    secondary_strategy: p.secondaryStrategy ?? null,
    vision_statement: p.visionStatement ?? null,
    current_constraint: p.currentConstraint ?? null,
    goal_feasibility: p.goalFeasibility ?? null,
    goal_is_custom: p.goalIsCustom ?? false,
    goal_notes: p.goalNotes ?? null,
    passive_income_target: p.passiveIncomeTarget ?? null,
    portfolio_value_target: p.portfolioValueTarget ?? null,
    portfolio_target_date: dateOrNull(p.portfolioTargetDate),
    retirement_age: p.retirementAge ?? null,
    retirement_income_target: p.retirementIncomeTarget ?? null,
    ppor_balance: p.pporBalance ?? null,
    ppor_payoff_date: dateOrNull(p.pporPayoffDate),
    reduced_income_periods: p.reducedIncomePeriods ?? [],
    household_members: p.householdMembers ?? [],
    relationships: p.relationships ?? [],
    team_contacts: p.teamContacts ?? [],
    verification_documents: p.verificationDocuments ?? [],
    tax_documents: p.taxDocuments ?? [],
    payslips: p.payslips ?? [],
    financial_goals: [],
    payg_variation_approved: p.paygVariationApproved ?? false,
    payg_variation_override: p.paygVariationOverride ?? null,
    // B103 Phase 4b
    payg_withheld: p.paygWithheld ?? null,
  };
}

// ─── Property CRUD ────────────────────────────────────────────────────────────

export async function fetchProperties(userId: string): Promise<PropertyRecord[]> {
  if (!guard()) return [];
  const { data, error } = await supabase!
    .from("properties")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("[supabaseStorage] fetchProperties:", error.message);
    return [];
  }
  return (data ?? []).map(rowToProperty);
}

export async function upsertProperty(p: PropertyRecord, userId: string): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  // Layer 2 null-address guard — see ARCHITECTURE §7. Properties without a
  // non-empty address must never be persisted to Supabase. App.tsx has the
  // primary (Layer 1) guard before the call; this is the defensive backstop
  // for any future creation/update path that forgets it.
  if (!p.address || !p.address.trim()) {
    console.warn("[supabaseStorage] upsertProperty blocked — null/empty address", { id: p.id });
    return { ok: false, error: "Empty address" };
  }
  const row = propertyToRow(p, userId);
  const { error } = await supabase!.from("properties").upsert(row, { onConflict: "id" });
  if (error) {
    console.error("[supabaseStorage] upsertProperty:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function upsertProperties(properties: PropertyRecord[], userId: string): Promise<void> {
  if (!guard() || properties.length === 0) return;
  // Layer 2 null-address guard — same rule as upsertProperty above. Filter
  // out any rows without a non-empty address before the bulk upsert.
  const validProperties = properties.filter((p) => p.address && p.address.trim().length > 0);
  if (validProperties.length < properties.length) {
    console.warn(
      `[supabaseStorage] upsertProperties dropped ${properties.length - validProperties.length} row(s) with null/empty address`
    );
  }
  if (validProperties.length === 0) return;
  const rows = validProperties.map((p) => propertyToRow(p, userId));
  const { error } = await supabase!.from("properties").upsert(rows, { onConflict: "id" });
  if (error) {
    console.error("[supabaseStorage] upsertProperties:", error.message);
    throw new Error(error.message);
  }
}

export async function deleteSupabaseProperty(id: string): Promise<void> {
  if (!guard()) return;
  const { error } = await supabase!.from("properties").delete().eq("id", id);
  if (error) console.error("[supabaseStorage] deleteProperty:", error.message);
}

// ─── Structure CRUD ───────────────────────────────────────────────────────────

export async function fetchStructures(userId: string): Promise<Structure[]> {
  if (!guard()) return [];
  const { data, error } = await supabase!
    .from("structures")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("[supabaseStorage] fetchStructures:", error.message);
    return [];
  }
  return (data ?? []).map(rowToStructure);
}

export async function upsertStructure(s: Structure, userId: string): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  const row = structureToRow(s, userId);
  const { error } = await supabase!.from("structures").upsert(row, { onConflict: "id" });
  if (error) {
    console.error("[supabaseStorage] upsertStructure:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function upsertStructures(structures: Structure[], userId: string): Promise<void> {
  if (!guard() || structures.length === 0) return;
  const rows = structures.map((s) => structureToRow(s, userId));
  const { error } = await supabase!.from("structures").upsert(rows, { onConflict: "id" });
  if (error) {
    console.error("[supabaseStorage] upsertStructures:", error.message);
    throw new Error(error.message);
  }
}

export async function deleteSupabaseStructure(id: string): Promise<void> {
  if (!guard()) return;
  const { error } = await supabase!.from("structures").delete().eq("id", id);
  if (error) console.error("[supabaseStorage] deleteStructure:", error.message);
}

// ─── Profile CRUD ─────────────────────────────────────────────────────────────

export async function fetchProfile(userId: string): Promise<InvestorProfile | null> {
  if (!guard()) return null;
  const { data, error } = await supabase!
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) {
    console.error("[supabaseStorage] fetchProfile:", error.message);
    return null;
  }
  return data ? rowToProfile(data) : null;
}

export async function upsertProfile(p: InvestorProfile, userId: string): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  const row = profileToRow(p, userId);
  const { error } = await supabase!.from("profiles").upsert(row, { onConflict: "user_id" });
  if (error) {
    console.error("[supabaseStorage] upsertProfile:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// ─── Budget CRUD ──────────────────────────────────────────────────────────────

export async function fetchBudget(
  userId: string
): Promise<{ settings: BudgetSettings; categories: BudgetCategory[] } | null> {
  if (!guard()) return null;
  const { data, error } = await supabase!
    .from("budget")
    .select("settings, categories")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) {
    console.error("[supabaseStorage] fetchBudget:", error.message);
    return null;
  }
  if (!data) return null;
  const rawSettings = data.settings as BudgetSettings;
  // Default missing bucket.type to "custom" (Phase 1 follow-up — JSONB Mapper Rule)
  const buckets = (rawSettings.buckets ?? []).map((b) => ({ ...b, type: b.type ?? ("custom" as const) }));
  const settings: BudgetSettings = { ...rawSettings, buckets };
  return { settings, categories: data.categories as BudgetCategory[] };
}

export async function upsertBudget(
  data: { settings: BudgetSettings; categories: BudgetCategory[] },
  userId: string
): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  const { error } = await supabase!.from("budget").upsert(
    { user_id: userId, settings: data.settings, categories: data.categories },
    { onConflict: "user_id" }
  );
  if (error) {
    console.error("[supabaseStorage] upsertBudget:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// ─── App settings CRUD ────────────────────────────────────────────────────────

export async function fetchAppSettings(userId: string): Promise<Partial<AppSettings> | null> {
  if (!guard()) return null;
  const { data, error } = await supabase!
    .from("app_settings")
    .select("max_lvr, lender_type, include_smsf_in_portfolio, portfolio_buffer_percent, rent_growth_assumption, funding_sources, personal_contribution_override, property_bucket_seeded, expense_buffer_percent, disabled_alert_categories, digest_frequency")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) {
    console.error("[supabaseStorage] fetchAppSettings:", error.message);
    return null;
  }
  if (!data) return null;
  return {
    lvrConservative: 70,
    lvrStandard: data.max_lvr ?? 80,
    lvrAggressive: 90,
    lenderType: (data.lender_type ?? "major-bank") as AppSettings["lenderType"],
    includeSMSFInPortfolio: data.include_smsf_in_portfolio ?? true,
    portfolioBufferPercent: data.portfolio_buffer_percent ?? 10,
    rentGrowthAssumption: data.rent_growth_assumption ?? 0.03,
    fundingSources: (data.funding_sources ?? []) as AppSettings["fundingSources"],
    propertyBucketSeeded: data.property_bucket_seeded ?? false,
    expenseBufferPercent: data.expense_buffer_percent ?? 10,
    disabledAlertCategories: data.disabled_alert_categories ?? [],
    digestFrequency: (data.digest_frequency ?? "weekly") as AppSettings["digestFrequency"],
  };
}

export async function upsertAppSettings(s: AppSettings, userId: string): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  const { error } = await supabase!.from("app_settings").upsert(
    {
      user_id: userId,
      max_lvr: s.lvrStandard ?? 80,
      lender_type: s.lenderType ?? "major-bank",
      include_smsf_in_portfolio: s.includeSMSFInPortfolio ?? true,
      portfolio_buffer_percent: s.portfolioBufferPercent ?? 10,
      rent_growth_assumption: s.rentGrowthAssumption ?? 0.03,
      funding_sources: s.fundingSources ?? [],
      property_bucket_seeded: s.propertyBucketSeeded ?? false,
      expense_buffer_percent: s.expenseBufferPercent ?? 10,
      disabled_alert_categories: s.disabledAlertCategories ?? [],
      digest_frequency: s.digestFrequency ?? "weekly",
    },
    { onConflict: "user_id" }
  );
  if (error) {
    console.error("[supabaseStorage] upsertAppSettings:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// ─── Batch fetch ──────────────────────────────────────────────────────────────

export async function fetchAllData(userId: string): Promise<{
  properties: PropertyRecord[];
  structures: Structure[];
  profile: InvestorProfile | null;
  budget: { settings: BudgetSettings; categories: BudgetCategory[] } | null;
  settings: Partial<AppSettings> | null;
}> {
  const [properties, structures, profile, budget, settings] = await Promise.all([
    fetchProperties(userId),
    fetchStructures(userId),
    fetchProfile(userId),
    fetchBudget(userId),
    fetchAppSettings(userId),
  ]);
  return { properties, structures, profile, budget, settings };
}

// ─── Property Actuals (per property, per FY) ─────────────────────────────────

export interface PropertyActuals {
  propertyId: string;
  fyYear: string;
  incomeRows: IncomeRow[];
  expenseRows: ExpenseRow[];
  weeksRented: number;
  notes: string;
}

export async function fetchActualsForProperty(propertyId: string, fyYear: string): Promise<PropertyActuals | null> {
  if (!guard()) return null;
  const { data, error } = await supabase!
    .from("property_actuals")
    .select("*")
    .eq("property_id", propertyId)
    .eq("fy_year", fyYear)
    .maybeSingle();
  if (error) {
    console.error("[supabaseStorage] fetchActualsForProperty:", error.message);
    return null;
  }
  if (!data) return null;
  return {
    propertyId: data.property_id,
    fyYear: data.fy_year,
    incomeRows: (data.income_rows ?? []) as IncomeRow[],
    expenseRows: (data.expense_rows ?? []) as ExpenseRow[],
    weeksRented: typeof data.weeks_rented === "number" ? data.weeks_rented : 52,
    notes: typeof data.notes === "string" ? data.notes : "",
  };
}

export async function upsertActuals(actuals: PropertyActuals, userId: string): Promise<{ ok: boolean; error?: string }> {
  if (!guard()) return { ok: false, error: "Supabase not connected" };
  const { error } = await supabase!.from("property_actuals").upsert(
    {
      property_id: actuals.propertyId,
      user_id: userId,
      fy_year: actuals.fyYear,
      income_rows: actuals.incomeRows,
      expense_rows: actuals.expenseRows,
      weeks_rented: actuals.weeksRented,
      notes: actuals.notes,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "property_id,fy_year" }
  );
  if (error) {
    console.error("[supabaseStorage] upsertActuals:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function fetchAllActualsForFY(userId: string, fyYear: string): Promise<Map<string, PropertyActuals>> {
  const result = new Map<string, PropertyActuals>();
  if (!guard()) return result;
  const { data, error } = await supabase!
    .from("property_actuals")
    .select("*")
    .eq("user_id", userId)
    .eq("fy_year", fyYear);
  if (error) {
    console.error("[supabaseStorage] fetchAllActualsForFY:", error.message);
    return result;
  }
  for (const row of data ?? []) {
    result.set(row.property_id, {
      propertyId: row.property_id,
      fyYear: row.fy_year,
      incomeRows: (row.income_rows ?? []) as IncomeRow[],
      expenseRows: (row.expense_rows ?? []) as ExpenseRow[],
      weeksRented: typeof row.weeks_rented === "number" ? row.weeks_rented : 52,
      notes: typeof row.notes === "string" ? row.notes : "",
    });
  }
  return result;
}
